import React, {useState} from 'react';
import {Button, Form, FloatingLabel} from 'react-bootstrap';

interface TagInputFieldProps {
  label: string;
  value: string[];
  onChange: (newValues: string[]) => void;
  isPhoneOrFax?: boolean; // Determines whether to apply phone formatting
  disabled?: boolean;
  required?: boolean;
  handleRemove?: () => void;
  isEditable?: boolean;
}

const SingleTagInputField: React.FC<TagInputFieldProps> = ({
  label,
  value,
  onChange,
  isPhoneOrFax = false,
  disabled = false,
  required = false,
  handleRemove,
  isEditable = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const formatPhoneNumber = (num: string): string => {
    let cleaned = num.replace(/\D/g, ''); // Remove all non-numeric characters
    if (cleaned.length > 10) {
      cleaned = cleaned.slice(0, 10); // Limit to 10 digits
    }
    if (cleaned.length > 6) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`;
    } else if (cleaned.length > 3) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const handleAddValue = () => {
    let formattedValue = inputValue.trim();
    if (formattedValue === '' || disabled) return;

    if (isPhoneOrFax) {
      formattedValue = formatPhoneNumber(formattedValue);
      if (!/^\d{3}-\d{3}-\d{4}$/.test(formattedValue)) {
        setErrorMessage('Invalid format. Use XXX-XXX-XXXX.');
        return;
      }
    }

    onChange([...value, formattedValue]);
    setInputValue('');
    setErrorMessage('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // Prevent default behavior (e.g., space typing)
      handleAddValue();
    }
  };

  const handleEdit = (index: number) => {
    if (!disabled) {
      setEditingIndex(index);
      setEditText(value[index]);
      setErrorMessage(''); // Clear error when starting a new edit
    }
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null && editText.trim() !== '' && !disabled) {
      let formattedValue = editText.trim();

      if (isPhoneOrFax) {
        formattedValue = formatPhoneNumber(formattedValue);
        if (!/^\d{3}-\d{3}-\d{4}$/.test(formattedValue)) {
          setErrorMessage('Invalid format. Use XXX-XXX-XXXX.');
          return;
        }
      }

      const updatedValues = [...value];
      updatedValues[editingIndex] = formattedValue;
      onChange(updatedValues);
      setEditingIndex(null);
      setEditText('');
      setErrorMessage('');
    }
  };

  const handleRemoveNew = (index: number) => {
    if (!disabled) {
      onChange(value.filter((_, i) => i !== index));
      if (editingIndex === index) {
        setEditingIndex(null);
        setEditText('');
      }
    }
  };

  return (
    <Form.Group className='mb-0 position-relative'>
      <Form.Control as='div' className='d-flex flex-wrap '>
        {value.map((val, index) => (
          <span
            key={index}
            className='badge me-1 d-flex align-items-center badge-pills'
            style={{
              borderRadius: '15px',
              padding: '5px 10px',
              marginTop: '0px',
            }}
          >
            {val}
            {!disabled && (
              <>
                <Button
                  variant=''
                  className='p-0 ms-1 d-flex align-items-center border-0'
                  onClick={() => handleEdit(index)}
                >
                  <span className='material-icons'>edit</span>
                </Button>
                {isEditable ? (
                  <Button
                    variant=''
                    className='p-0 ms-1 d-flex align-items-center'
                    onClick={() => {
                      if (handleRemove) {
                        handleRemove(); // Call parent's handler
                      }
                    }}
                  >
                    <span className='material-icons'>close</span>
                  </Button>
                ) : (
                  <Button
                    variant=''
                    className='p-0 ms-1 d-flex align-items-center'
                    onClick={() => handleRemoveNew(index)}
                  >
                    <span className='material-icons'>close</span>
                  </Button>
                )}
              </>
            )}
          </span>
        ))}
        <input
          type='text'
          className='border-0 flex-grow-1 '
          placeholder=' '
          value={inputValue}
          maxLength={isPhoneOrFax ? 12 : 50} // 12 characters including dashes
          disabled={disabled}
          onChange={(e) => {
            let newValue = e.target.value;
            if (isPhoneOrFax) {
              newValue = formatPhoneNumber(newValue);
            } else {
              newValue = newValue.replace(/\s/g, ''); // Remove spaces for email
            }
            if (newValue.startsWith(' ')) return; // Prevent early spaces
            setInputValue(newValue);
          }}
          onBlur={handleAddValue}
          onKeyDown={handleKeyDown}
          style={{
            outline: 'none',
            minWidth: '50px',
            width: '50px',
            background: 'transparent',
          }}
        />
      </Form.Control>

      {/* Edit field with error message */}
      {editingIndex !== null && !disabled && (
        <div
          className='d-flex flex-row position-absolute bg-white p-2 shadow-lg w-75'
          style={{zIndex: 9}}
        >
          <Form.Control
            type='text'
            className='me-auto'
            value={editText}
            maxLength={isPhoneOrFax ? 12 : 50} // 12 characters including dashes
            onChange={(e) => {
              let newValue = e.target.value;
              if (isPhoneOrFax) {
                newValue = formatPhoneNumber(newValue);
              } else {
                newValue = newValue.replace(/\s/g, ''); // Remove spaces for email
              }
              if (newValue.startsWith(' ')) return; // Prevent early spaces
              setEditText(newValue);
              setErrorMessage(''); // Clear error when editing
            }}
            autoFocus
          />

          {errorMessage && (
            <div style={{color: 'red', fontSize: '14px', marginTop: '4px'}}>
              {errorMessage}
            </div>
          )}

          <div className='d-flex'>
            <button
              type='button'
              className='btn d-flex align-items-center btn-text-border mx-2 p-1'
              onClick={handleSaveEdit}
            >
              <span className='material-icons mr-2'>check</span>
            </button>

            <button
              type='button'
              className='btn d-flex align-items-center btn-text-border mx-1 p-1'
              onClick={() => {
                setEditingIndex(null);
                setEditText('');
                setErrorMessage('');
              }}
            >
              <span className='material-icons mr-2'>close</span>
            </button>
          </div>
        </div>
      )}
    </Form.Group>
  );
};

export default SingleTagInputField;
